import { MapPin, MessageCircle, Sparkles, Search, Sofa, Phone, UserPlus, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

export const UserCard = ({ user, onConnect, navigate, isRecommended = false, isSearchResult = false, currentUserPremium = false }) => {
  if (!user || !user.id) {
    console.warn('UserCard: Invalid user data', user);
    return null;
  }

  const userName = user.name || 'Unknown User';
  const userInitial = userName.charAt(0).toUpperCase() || '?';

  return (
    <div
      data-testid={`user-card-${user.id}`}
      className={`glass-dark rounded-3xl p-6 hover:shadow-xl transition-all duration-300 relative overflow-hidden ${
        isSearchResult ? 'ring-2 ring-teal ring-offset-2' : ''
      }`}
      style={{ maxWidth: '100%' }}
    >
      {isSearchResult && (
        <div className="absolute top-4 right-4">
          <Badge className="bg-teal text-white font-semibold">
            <Search className="w-3 h-3 mr-1" />
            Found
          </Badge>
        </div>
      )}
      {isRecommended && !isSearchResult && (
        <div className="absolute top-4 right-4">
          <Badge className="bg-white text-black border-2 border-teal font-semibold">
            <Sparkles className="w-3 h-3 mr-1 text-teal" />
            Recommended
          </Badge>
        </div>
      )}

      {user.is_premium && user.offers_couch_surfing && (
        <div className="absolute top-4 left-4">
          <Badge className="bg-teal text-white font-semibold">
            <Sofa className="w-3 h-3 mr-1" />
            Host Available
          </Badge>
        </div>
      )}

      <div className="flex flex-col items-center text-center mb-4">
        <Avatar
          className="w-24 h-24 mb-4 cursor-pointer border-4 border-white shadow-lg"
          onClick={() => navigate(`/user/${user.id}`)}
        >
          {user.profile_photo ? (
            <AvatarImage
              src={user.profile_photo}
              alt={user.name}
              loading="lazy"
              className="object-cover w-full h-full"
            />
          ) : null}
          <AvatarFallback className="bg-white text-black text-2xl font-bold border-4 border-teal shadow-lg">
            {userInitial}
          </AvatarFallback>
        </Avatar>
        <h3
          className="text-xl font-bold cursor-pointer hover:text-coral transition-colors"
          onClick={() => navigate(`/user/${user.id}`)}
        >
          {userName}
        </h3>
        {user.location?.city && (
          <div className="flex items-center space-x-1 text-gray-600 mt-1">
            <MapPin className="w-4 h-4" />
            <span className="text-sm">{user.location.city}</span>
          </div>
        )}
      </div>

      {user.bio && (
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{user.bio}</p>
      )}

      {user.interests && user.interests.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {user.interests.slice(0, 3).map((interest, idx) => (
            <Badge key={`interest-${interest}`} variant="secondary" className="bg-peach/20 text-coral border-none">
              {interest}
            </Badge>
          ))}
        </div>
      )}

      <div className="flex flex-col space-y-2">
        <Button
          data-testid={`connect-btn-${user.id}`}
          onClick={() => onConnect(user.id)}
          className="w-full bg-coral hover:bg-coral-dark text-white rounded-full font-semibold py-3"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Connect
        </Button>

        <div className="flex space-x-2">
          <Button
            data-testid={`message-btn-${user.id}`}
            onClick={() => navigate(`/messages/${user.id}`)}
            variant="outline"
            className="flex-1 border-2 border-teal text-teal hover:bg-teal hover:text-white rounded-full"
          >
            <MessageCircle className="w-4 h-4 mr-1" />
            Message
          </Button>
          <Button
            data-testid={`call-btn-${user.id}`}
            onClick={() => {
              if (!currentUserPremium) {
                toast.error('Upgrade to Premium to make calls');
                navigate('/subscription');
                return;
              }
              navigate(`/messages/${user.id}?call=true`);
            }}
            variant="outline"
            className={`border-2 rounded-full px-4 ${currentUserPremium ? 'border-coral text-coral hover:bg-coral hover:text-white' : 'border-gray-300 text-gray-400'}`}
            title={currentUserPremium ? 'Call' : 'Premium only'}
          >
            <Phone className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
